import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import './ClientProjects.css';

interface Project {
  id: number;
  name: string;
  location: string;
  status: 'Planning' | 'In Progress' | 'Completed';
  startDate: string;
  budget: string;
}

const ClientProjects: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [newProject, setNewProject] = useState({
    name: '',
    location: '',
    status: 'Planning' as 'Planning' | 'In Progress' | 'Completed',
    startDate: '',
    budget: ''
  });

  useEffect(() => {
    fetchProjects();
  }, [clientId]);

  const fetchProjects = async () => {
    try {
      console.log('Fetching projects for client:', clientId);
      setLoading(true);
      const response = await api.get(`/projects/client/${clientId}`);
      console.log('Projects API response:', response.data);
      console.log('Number of projects:', response.data?.length);
      setProjects(response.data);
      console.log('Projects state updated');
    } catch (error: any) {
      console.error('Error fetching projects:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Planning': return '#FFA726';
      case 'In Progress': return '#42A5F5';
      case 'Completed': return '#66BB6A';
      default: return '#999';
    }
  };

  const handleProjectClick = (projectId: number) => {
    navigate(`/client/${clientId}/project/${projectId}`);
  };

  const handleAddProject = async () => {
    if (!newProject.name || !newProject.location || !newProject.startDate || !newProject.budget) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (editingProject) {
        // Update existing project
        await api.put(`/projects/${editingProject.id}`, {
          name: newProject.name,
          location: newProject.location,
          status: newProject.status,
          startDate: newProject.startDate,
          budget: newProject.budget
        });
      } else {
        // Create new project
        await api.post('/projects', {
          name: newProject.name,
          location: newProject.location,
          status: newProject.status,
          startDate: newProject.startDate,
          budget: newProject.budget,
          clientId: parseInt(clientId!)
        });
      }

      // Refresh projects list
      await fetchProjects();
      setShowAddModal(false);
      setEditingProject(null);
      setNewProject({ name: '', location: '', status: 'Planning', startDate: '', budget: '' });
    } catch (error: any) {
      alert(error.response?.data?.message || `Failed to ${editingProject ? 'update' : 'add'} project`);
      console.error(`Error ${editingProject ? 'updating' : 'adding'} project:`, error);
    }
  };

  const handleEditProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setEditingProject(project);
    setNewProject({
      name: project.name,
      location: project.location,
      status: project.status,
      startDate: project.startDate,
      budget: project.budget
    });
    setShowAddModal(true);
  };

  const handleDeleteProject = async (projectId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await api.delete(`/projects/${projectId}`);
      // Refresh projects list
      await fetchProjects();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete project');
      console.error('Error deleting project:', error);
    }
  };

  return (
    <div className="client-projects-container">
      <header className="client-projects-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/clients')}>
            ← Back to Clients
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          <h1>Client Projects</h1>
        </div>
        <button className="btn-add-project" onClick={() => setShowAddModal(true)}>+ Add New Project</button>
      </header>

      <div className="client-projects-content">
        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading projects...</div>}

        {!loading && projects.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>No projects yet</p>
            <p style={{ fontSize: '14px' }}>Click "+ Add New Project" to get started</p>
          </div>
        )}

        {!loading && projects.length > 0 && (
          <div className="projects-grid">
            {projects.map((project) => (
              <div
                key={project.id}
                className="project-card"
                onClick={() => handleProjectClick(project.id)}
              >
                <div className="project-header">
                  <h3>{project.name}</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(project.status) }}
                    >
                      {project.status}
                    </span>
                    <button
                      className="btn-edit"
                      onClick={(e) => handleEditProject(project, e)}
                      title="Edit project"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-delete"
                      onClick={(e) => handleDeleteProject(project.id, e)}
                      title="Delete project"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <div className="project-details">
                  <div className="detail-item">
                    <span className="detail-icon">📍</span>
                    <span>{project.location}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">📅</span>
                    <span>{new Date(project.startDate).toLocaleDateString()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">💰</span>
                    <span>{project.budget}</span>
                  </div>
                </div>
                <button className="btn-view-project">View Project Details →</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Project Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setEditingProject(null);
          setNewProject({ name: '', location: '', status: 'Planning', startDate: '', budget: '' });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
              <button className="btn-close" onClick={() => {
                setShowAddModal(false);
                setEditingProject(null);
                setNewProject({ name: '', location: '', status: 'Planning', startDate: '', budget: '' });
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  placeholder="Enter project name"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  placeholder="Enter location"
                  value={newProject.location}
                  onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select
                  value={newProject.status}
                  onChange={(e) => setNewProject({ ...newProject, status: e.target.value as 'Planning' | 'In Progress' | 'Completed' })}
                >
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={newProject.startDate}
                  onChange={(e) => setNewProject({ ...newProject, startDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Budget *</label>
                <input
                  type="text"
                  placeholder="Enter budget (e.g., ₹50,00,000)"
                  value={newProject.budget}
                  onChange={(e) => setNewProject({ ...newProject, budget: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setShowAddModal(false);
                setEditingProject(null);
                setNewProject({ name: '', location: '', status: 'Planning', startDate: '', budget: '' });
              }}>Cancel</button>
              <button className="btn-submit" onClick={handleAddProject}>
                {editingProject ? 'Update Project' : 'Add Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientProjects;
